import React, { createContext, useState, useLayoutEffect } from 'react';
import api from "../api/apiClient";

export const UserContext = createContext();

export const UserProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isPrelaunch, setIsPrelaunch] = useState(false);

  const addNotification = (message) => setNotifications((prev) => [...prev, message]);
  const clearNotifications = () => setNotifications([]);
  const updateSelectedCategory = (category) => setSelectedCategory(category);

  const fetchUserFromDB = async (userId, retryCount = 3) => {
    for (let i = 0; i < retryCount; i++) {
      try {
        console.log(`Försök ${i + 1} att hämta användare med ID: ${userId}`);
        const { data } = await api.get(`/api/get-user?user_id=${userId}`);
        return data;
      } catch (error) {
        console.error(`Försök ${i + 1} misslyckades:`, error);
        if (i === retryCount - 1) throw error;
        await new Promise(resolve => setTimeout(resolve, 1000 * (i + 1)));
      }
    }
    return null;
  };

  // ✅ Bytt till useLayoutEffect + delay innan loading = false om user saknas
  useLayoutEffect(() => {
    let isMounted = true;

    const checkAndFetchUser = async () => {
      const userId = localStorage.getItem("user_id");
      console.log("Kontrollerar localStorage user_id:", userId);

      if (!userId) {
        console.log("Ingen user_id i localStorage – användaren är inte inloggad");
        setTimeout(() => {
          if (isMounted) {
            setCurrentUser(null);
            setLoading(false);
          }
        }, 100);
        return;
      }

      try {
        const userData = await fetchUserFromDB(userId);
        if (isMounted) {
          if (userData) {
            setCurrentUser(userData);
          } else {
            console.error("Kunde inte hämta användardata – loggar ut användaren");
            localStorage.removeItem("user_id");
            setCurrentUser(null);
          }
        }
      } catch (error) {
        if (isMounted) {
          console.error("Fel vid hämtning av användardata:", error);
          setCurrentUser(null);
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    checkAndFetchUser();
    return () => { isMounted = false; };
  }, []);

  const handleSuccessfulLogin = async (user) => {
    const userId = user.username;
    if (userId) {
      try {
        const userData = await fetchUserFromDB(userId);
        if (userData) {
          localStorage.setItem("user_id", userId);
          setCurrentUser(userData);
          setIsLoginModalOpen(false);
        }
      } catch (error) {
        console.error("Fel vid hämtning av användardata efter inloggning:", error);
      }
    }
  };

  const logout = () => {
    localStorage.removeItem("user_id");
    setCurrentUser(null);
  };

  const updateCredits = (newCredits) => {
    if (currentUser) {
      setCurrentUser({ ...currentUser, credits: newCredits });
    }
  };

  return (
    <UserContext.Provider
      value={{
        currentUser,
        loading,
        isLoginModalOpen,
        updateCredits,
        notifications,
        addNotification,
        clearNotifications,
        setIsLoginModalOpen,
        handleSuccessfulLogin,
        logout,
        selectedCategory,
        updateSelectedCategory,
        isPrelaunch,
        setIsPrelaunch,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};
